/**
 * CalendarGuideModal - Guia completo do Calendário de Sangria
 * Funcionalidades documentadas:
 * - Visão geral, registros, safra, ethrel, equipe, relatórios, dicas
 * - Totalmente internacionalizado (PT/EN/ES)
 */

import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { useTranslation } from "../translations";

const CalendarGuideModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', icon: '📋', title: t('cgOverview') },
    { id: 'records', icon: '📝', title: t('cgDailyRecords') },
    { id: 'safra', icon: '📊', title: t('cgHarvestSummary') },
    { id: 'ethrel', icon: '⚡', title: t('cgEthrelControl') },
    { id: 'team', icon: '👥', title: t('cgTeamManagement') },
    { id: 'reports', icon: '📈', title: t('cgReports') },
    { id: 'tips', icon: '💡', title: t('cgTips') },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">{t('cgWhatIsCalendar')}</h4>
              <p className="text-green-700 text-sm">{t('cgCalendarDesc')}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">🏠</span>
                  <span className="font-medium text-blue-800">{t('cgProperties')}</span>
                </div>
                <p className="text-blue-700 text-xs">{t('cgPropertiesDesc')}</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">📅</span>
                  <span className="font-medium text-purple-800">{t('cgVisualCalendar')}</span>
                </div>
                <p className="text-purple-700 text-xs">{t('cgVisualCalendarDesc')}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">📊</span>
                  <span className="font-medium text-orange-800">{t('cgAutoSummaries')}</span>
                </div>
                <p className="text-orange-700 text-xs">{t('cgAutoSummariesDesc')}</p>
              </div>
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">👥</span>
                  <span className="font-medium text-teal-800">{t('cgTeamMgmt')}</span>
                </div>
                <p className="text-teal-700 text-xs">{t('cgTeamMgmtDesc')}</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h5 className="font-medium text-amber-800 mb-2">{t('cgFirstSteps')}</h5>
              <ol className="text-amber-700 text-sm space-y-1 list-decimal list-inside">
                <li>{t('cgFirstStep1')}</li>
                <li>{t('cgFirstStep2')}</li>
                <li>{t('cgFirstStep3')}</li>
                <li>{t('cgFirstStep4')}</li>
              </ol>
            </div>
          </div>
        );

      case 'records':
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">{t('cgRecHowToRegister')}</h4>
              <p className="text-blue-700 text-sm mb-3">{t('cgRecClickAnyDay')}</p>
            </div>
            <div className="space-y-3">
              <h5 className="font-medium text-gray-800">{t('cgRecAvailableFields')}</h5>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex items-start gap-3 p-2 bg-green-50 rounded-lg">
                  <img src="/icons/faca_sangria.png" alt="" className="w-6 h-6 mt-0.5" />
                  <div>
                    <span className="font-medium text-green-800">{t('cgRecBleeding')}</span>
                    <p className="text-green-700 text-xs">{t('cgRecBleedingDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-amber-50 rounded-lg">
                  <span className="text-xl">📦</span>
                  <div>
                    <span className="font-medium text-amber-800">{t('cgRecProduction')}</span>
                    <p className="text-amber-700 text-xs">{t('cgRecProductionDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-sky-50 rounded-lg">
                  <span className="text-xl">🌧️</span>
                  <div>
                    <span className="font-medium text-sky-800">{t('cgRecRain')}</span>
                    <p className="text-sky-700 text-xs">{t('cgRecRainDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-blue-50 rounded-lg">
                  <span className="text-xl">🔄</span>
                  <div>
                    <span className="font-medium text-blue-800">{t('cgRecReplacement')}</span>
                    <p className="text-blue-700 text-xs">{t('cgRecReplacementDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-purple-50 rounded-lg">
                  <span className="text-xl">🧪</span>
                  <div>
                    <span className="font-medium text-purple-800">{t('cgRecFungicide')}</span>
                    <p className="text-purple-700 text-xs">{t('cgRecFungicideDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-orange-50 rounded-lg">
                  <span className="text-xl">⚡</span>
                  <div>
                    <span className="font-medium text-orange-800">Ethrel</span>
                    <p className="text-orange-700 text-xs">{t('cgRecEthrelDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <h5 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                <span>👥</span> {t('cgRecDuplicate')}
              </h5>
              <p className="text-green-700 text-sm">{t('cgRecDuplicateDesc')}</p>
            </div>
          </div>
        );

      case 'safra':
        return (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-2">{t('cgSafraTitle')}</h4>
              <p className="text-purple-700 text-sm">{t('cgSafraDesc')}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h5 className="font-medium text-amber-800 mb-2">{t('cgSafraWhatIs')}</h5>
              <p className="text-amber-700 text-sm mb-2">{t('cgSafraWhatIsDesc')}</p>
              <p className="text-amber-700 text-sm">{t('cgSafraConfigNote')}</p>
            </div>
            <div className="space-y-2">
              <h5 className="font-medium text-gray-800">{t('cgSafraMetrics')}</h5>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-gray-50 rounded text-center">
                  <div className="text-lg font-bold text-green-600">📦</div>
                  <div className="text-xs text-gray-600">{t('cgSafraTotalProd')}</div>
                </div>
                <div className="p-2 bg-gray-50 rounded text-center">
                  <div className="text-lg font-bold text-blue-600">🌧️</div>
                  <div className="text-xs text-gray-600">{t('cgSafraTotalRain')}</div>
                </div>
                <div className="p-2 bg-gray-50 rounded text-center">
                  <div className="text-lg font-bold text-purple-600">🔄</div>
                  <div className="text-xs text-gray-600">{t('cgSafraTotalReplacements')}</div>
                </div>
                <div className="p-2 bg-gray-50 rounded text-center">
                  <div className="text-lg font-bold text-orange-600">⚡</div>
                  <div className="text-xs text-gray-600">{t('cgSafraEthrelApps')}</div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h5 className="font-medium text-blue-800 mb-2">{t('cgSafraComparative')}</h5>
              <p className="text-blue-700 text-sm">{t('cgSafraComparativeDesc')}</p>
              <ul className="text-blue-700 text-sm mt-2 space-y-1 list-disc list-inside">
                <li><span className="text-green-600">▲</span> {t('cgSafraIncrease')}</li>
                <li><span className="text-red-600">▼</span> {t('cgSafraDecrease')}</li>
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <h5 className="font-medium text-green-800 mb-2">{t('cgSafraConfigTitle')}</h5>
              <ol className="text-green-700 text-sm space-y-1 list-decimal list-inside">
                <li>{t('cgSafraStep1')}</li>
                <li>{t('cgSafraStep2')}</li>
                <li>{t('cgSafraStep3')}</li>
                <li>{t('cgSafraStep4')}</li>
              </ol>
            </div>
          </div>
        );

      case 'ethrel':
        return (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="font-semibold text-orange-800 mb-2">{t('cgEthrelTitle')}</h4>
              <p className="text-orange-700 text-sm">{t('cgEthrelDesc')}</p>
            </div>
            <div className="space-y-3">
              <h5 className="font-medium text-gray-800">{t('cgEthrelHow')}</h5>
              <div className="bg-white border rounded-lg p-3 space-y-3">
                {[
                  { step: '1', title: t('cgEthrelStep1'), desc: t('cgEthrelStep1Desc') },
                  { step: '2', title: t('cgEthrelStep2'), desc: t('cgEthrelStep2Desc') },
                  { step: '3', title: t('cgEthrelStep3'), desc: t('cgEthrelStep3Desc') },
                ].map(item => (
                  <div key={item.step} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">{item.step}</div>
                    <div>
                      <span className="font-medium">{item.title}</span>
                      <p className="text-gray-600 text-xs">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <h5 className="font-medium text-yellow-800 mb-2">{t('cgEthrelBanner')}</h5>
              <p className="text-yellow-700 text-sm mb-2">{t('cgEthrelBannerDesc')}</p>
              <ul className="text-yellow-700 text-sm space-y-1 list-disc list-inside">
                <li>{t('cgEthrelBannerItem1')}</li>
                <li>{t('cgEthrelBannerItem2')}</li>
                <li>{t('cgEthrelBannerItem3')}</li>
                <li>{t('cgEthrelBannerItem4')}</li>
              </ul>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <h5 className="font-medium text-red-800 mb-2">{t('cgEthrelColors')}</h5>
              <div className="space-y-2">
                <div className="flex items-center gap-2"><div className="w-4 h-4 bg-green-500 rounded"></div><span className="text-sm text-gray-700">{t('cgEthrelGreen')}</span></div>
                <div className="flex items-center gap-2"><div className="w-4 h-4 bg-yellow-500 rounded"></div><span className="text-sm text-gray-700">{t('cgEthrelYellow')}</span></div>
                <div className="flex items-center gap-2"><div className="w-4 h-4 bg-red-500 rounded"></div><span className="text-sm text-gray-700">{t('cgEthrelRed')}</span></div>
              </div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <h5 className="font-medium text-purple-800 mb-2">{t('cgEthrelConcentration')}</h5>
              <p className="text-purple-700 text-sm">{t('cgEthrelConcentrationDesc')}</p>
            </div>
          </div>
        );

      case 'team':
        return (
          <div className="space-y-4">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h4 className="font-semibold text-teal-800 mb-2">{t('cgTeamTitle')}</h4>
              <p className="text-teal-700 text-sm">{t('cgTeamTitleDesc')}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h5 className="font-medium text-blue-800 mb-2">{t('cgTeamDatabase')}</h5>
              <p className="text-blue-700 text-sm mb-2">{t('cgTeamDatabaseDesc')}</p>
              <ul className="text-blue-700 text-sm space-y-1 list-disc list-inside">
                <li>{t('cgTeamDbItem1')}</li>
                <li>{t('cgTeamDbItem2')}</li>
                <li>{t('cgTeamDbItem3')}</li>
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <h5 className="font-medium text-green-800 mb-2">{t('cgTeamReport')}</h5>
              <p className="text-green-700 text-sm mb-2">{t('cgTeamReportDesc')}</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  { icon: '🏆', color: 'green', label: t('cgTeamRanking') },
                  { icon: '📊', color: 'blue', label: t('cgTeamAvgTapper') },
                  { icon: '📅', color: 'purple', label: t('cgTeamDaysWorked') },
                  { icon: '⚡', color: 'orange', label: t('cgTeamProdPerDay') },
                ].map(item => (
                  <div key={item.label} className="p-2 bg-white rounded text-center border">
                    <div className={`font-bold text-${item.color}-600`}>{item.icon}</div>
                    <div className="text-xs text-gray-600">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h5 className="font-medium text-amber-800 mb-2">{t('cgTeamMultiple')}</h5>
              <p className="text-amber-700 text-sm mb-2">{t('cgTeamMultipleDesc')}</p>
              <ol className="text-amber-700 text-sm space-y-1 list-decimal list-inside">
                <li>{t('cgTeamMultiStep1')}</li>
                <li>{t('cgTeamMultiStep2')}</li>
                <li>{t('cgTeamMultiStep3')}</li>
                <li>{t('cgTeamMultiStep4')}</li>
              </ol>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <h5 className="font-medium text-purple-800 mb-2">{t('cgTeamIndTip')}</h5>
              <p className="text-purple-700 text-sm">{t('cgTeamIndTipDesc')}</p>
            </div>
          </div>
        );

      case 'reports':
        return (
          <div className="space-y-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-semibold text-indigo-800 mb-2">{t('cgRptTitle')}</h4>
              <p className="text-indigo-700 text-sm">{t('cgRptTitleDesc')}</p>
            </div>
            <div className="space-y-3">
              <h5 className="font-medium text-gray-800">{t('cgRptTypes')}</h5>
              {[
                { icon: '📊', color: 'blue', title: t('cgRptMonthlySummary'), desc: t('cgRptMonthlySummaryDesc') },
                { icon: '📈', color: 'purple', title: t('cgRptHarvestSummary'), desc: t('cgRptHarvestSummaryDesc') },
                { icon: '👥', color: 'green', title: t('cgRptTeamReport'), desc: t('cgRptTeamReportDesc') },
                { icon: '📋', color: 'orange', title: t('cgRptDailyTable'), desc: t('cgRptDailyTableDesc') },
              ].map(item => (
                <div key={item.title} className={`bg-${item.color}-50 border border-${item.color}-200 rounded-lg p-3`}>
                  <h6 className={`font-medium text-${item.color}-800 mb-2`}>{item.icon} {item.title}</h6>
                  <p className={`text-${item.color}-700 text-sm`}>{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h5 className="font-medium text-gray-800 mb-2">{t('cgRptExport')}</h5>
              <p className="text-gray-700 text-sm">{t('cgRptExportDesc')}</p>
            </div>
          </div>
        );

      case 'tips':
        return (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">{t('cgTipTitle')}</h4>
              <p className="text-yellow-700 text-sm">{t('cgTipTitleDesc')}</p>
            </div>
            <div className="space-y-3">
              {[
                { icon: '✅', color: 'green', title: t('cgTipDaily'), desc: t('cgTipDailyDesc') },
                { icon: '📱', color: 'blue', title: t('cgTipMobile'), desc: t('cgTipMobileDesc') },
                { icon: '👥', color: 'purple', title: t('cgTipIdentify'), desc: t('cgTipIdentifyDesc') },
                { icon: '⚡', color: 'orange', title: t('cgTipEthrelAlerts'), desc: t('cgTipEthrelAlertsDesc') },
                { icon: '🌧️', color: 'teal', title: t('cgTipRain'), desc: t('cgTipRainDesc') },
                { icon: '📊', color: 'indigo', title: t('cgTipReports'), desc: t('cgTipReportsDesc') },
              ].map(item => (
                <div key={item.title} className={`bg-${item.color}-50 border border-${item.color}-200 rounded-lg p-3`}>
                  <h5 className={`font-medium text-${item.color}-800 mb-2`}>{item.icon} {item.title}</h5>
                  <p className={`text-${item.color}-700 text-sm`}>{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 mt-4">
              <p className="text-gray-600 text-sm text-center">💬 {t('cgTipQuestions')}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden p-0">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-green-600 to-green-700">
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <span>📖</span>
            {t('cgModalTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex h-[calc(85vh-120px)]">
          <div className="w-48 border-r bg-gray-50 p-2 overflow-y-auto hidden md:block">
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                    activeSection === section.id
                      ? 'bg-green-100 text-green-800 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span>{section.icon}</span>
                  <span>{section.title}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="md:hidden border-b p-2 overflow-x-auto flex gap-1 absolute top-16 left-0 right-0 bg-white z-10">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                  activeSection === section.id
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {section.icon} {section.title}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-6 md:pt-6 pt-16">
            {renderContent()}
          </div>
        </div>

        <div className="border-t px-6 py-3 bg-gray-50 flex justify-between items-center">
          <p className="text-xs text-gray-500">{t('cgFooterText')}</p>
          <Button onClick={onClose} variant="outline" size="sm" data-testid="close-guide-btn">
            {t('cgClose')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarGuideModal;
